import {
    FileState,
    fileUploadStore,
} from 'components/ui/FileUpload/fileUploadStore';
import FileIcon from 'components/ui/FileUpload/components/FileIcon';
import classNames from 'classnames';
import Button from 'components/ui/Button';
import IconSVG from 'components/ui/Icon/IconSVG';
import ConfirmationOverlay from 'components/ui/Overlays/ConfirmationOverlay/ConfirmationOverlay';
import useModal from 'components/ui/ModalContainer/useModal';
import {IDocument} from 'types/documents';
import styles from './UploadedFile.module.scss';

export type FileManagement = {
    handleDownloadDocument?: (fileId?: string) => void;
    downloading: string[];
    deleting: string[];
    handleDeleteDocument?: ({
        fileId,
        baseUrl,
        uuid,
    }: {
        fileId?: string;
        baseUrl: string;
        uuid?: string;
    }) => void;
    keyForDownload: keyof Pick<IDocument, '@id' | 'hash'>;
};

interface IProps {
    fileState: FileState;
    baseUrl: string;
    uuid: string;
    fileManagementMethods: FileManagement;
}

const UploadedFile = ({
    fileState,
    baseUrl,
    uuid,
    fileManagementMethods,
}: IProps) => {
    const {openModalWithContent, closeModal} = useModal();
    const {
        handleDeleteDocument,
        handleDownloadDocument,
        downloading,
        deleting,
        keyForDownload,
    } = fileManagementMethods;
    const removeFile = fileUploadStore(state => state.removeFile);

    const handleDelete = () => {
        openModalWithContent(
            'confirmation',
            <ConfirmationOverlay
                title="Delete file?"
                description="This action cannot be reversed."
                onConfirm={() => {
                    handleDeleteDocument?.({
                        fileId: fileState.document?.[keyForDownload],
                        baseUrl,
                        uuid,
                    });
                    closeModal('confirmation');
                }}
                confirmVariant="error"
                confirmLabel="Delete"
                onCancel={() => closeModal('confirmation')}
            />,
        );
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                <div className={styles.iconContainer}>
                    <FileIcon
                        type={(
                            fileState.file?.type || fileState.document?.mimeType
                        )
                            ?.split('/')
                            .pop()}
                    />
                </div>
                <div className={styles.fileData} data-notranslate>
                    <p className={styles.name}>
                        {fileState.file?.name ||
                            fileState.document?.originalFileName}
                    </p>
                    {fileState.file ? (
                        <p className={styles.size}>
                            {(fileState.file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                    ) : null}
                </div>
                <div className={styles.actions}>
                    {fileState.document ? (
                        <>
                            {handleDownloadDocument ? (
                                <Button
                                    iconOnly
                                    variant="text"
                                    icon={<IconSVG name="download_old" />}
                                    onClick={() =>
                                        handleDownloadDocument(
                                            fileState.document?.[
                                                keyForDownload
                                            ],
                                        )
                                    }
                                    isLoading={
                                        fileState.document[keyForDownload]
                                            ? downloading.includes(
                                                  fileState.document[
                                                      keyForDownload
                                                  ],
                                              )
                                            : false
                                    }
                                />
                            ) : null}
                            {handleDeleteDocument ? (
                                <Button
                                    iconOnly
                                    variant="text"
                                    color="danger"
                                    icon={<IconSVG name="trash" />}
                                    onClick={() => handleDelete()}
                                    isLoading={
                                        fileState.document[keyForDownload]
                                            ? deleting.includes(
                                                  fileState.document[
                                                      keyForDownload
                                                  ],
                                              )
                                            : false
                                    }
                                />
                            ) : null}
                        </>
                    ) : (
                        <Button
                            iconOnly
                            variant="text"
                            icon={<IconSVG name="xMark" />}
                            onClick={() => {
                                removeFile(uuid);
                                handleDeleteDocument?.({
                                    fileId: fileState.document?.[
                                        keyForDownload
                                    ],
                                    baseUrl,
                                    uuid,
                                });
                            }}
                        />
                    )}
                </div>
                <div
                    className={classNames(styles.statusColor, {
                        [styles.loading]: fileState.status === 'uploading',
                        [styles.uploaded]: fileState.status === 'uploaded',
                        [styles.error]: fileState.status === 'error',
                    })}
                />
            </div>
            {fileState.errorMsg ? (
                <p className={styles.errorMsg}>{fileState.errorMsg}</p>
            ) : null}
        </div>
    );
};

export default UploadedFile;
